import React, { lazy, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MechanicsSummary from "../../components/MechanicsSummary/MechanicsSummary";
import CycleDefinition from "../../components/CycleDefinition/CycleDefinition";
import {
  resetAxesToCompare,
  selectCartesianReadOnlyMode,
  selectCartesianSteps,
  selectDynamics,
  selectMechanics,
  setAxisOptions,
  setAxisSelection,
  setCartesianCurrentStep,
  setDynamics,
  setDynamicsHash,
  setDynamicsStrokeLength
} from "../../store/slices/cartesian";
import Navigator from "../../../Common/layouts/Navigator/Navigator";
import t from "../../../Common/utils/translation/translator";
import { updateConfiguration } from "../../../Common/store/slices/common";
import CycleRunning from "../../components/CycleRunning/CycleRunning";
import { setConfiguration, setFetchedBundles, showSnackbar } from "../../../Retrofit/store/slices/retrofit";
import Spinner from "../../../Common/components/Icons/Spinner/Spinner";
import { generateMovesData, newMaxMovements } from "../../utils/func.utils";
import {
  DEFAULT_AXIS_OPTIONS,
  DEFAULT_AXIS_SELECTION,
  DIRECTIONS,
  getDynamicsHash
} from "../../store/data/cartesianData";
import { maxNumberCyclesPerHour, preCalcTotalCycleTime } from "../../utils/func.utils";

const GraphicalChart = lazy(() => import("../../components/GraphicalChart"));

const Dynamics = ({ ...props }) => {
  const dispatch = useDispatch();
  const steps = useSelector(selectCartesianSteps);
  const dynamics = useSelector(selectDynamics);
  const mechanics = useSelector(selectMechanics);
  const moves = dynamics.steps;
  const isReadOnlyMode = useSelector(selectCartesianReadOnlyMode);

  // Initialize selectedMove state as null to show all moves
  const [selectedMove, setSelectedMove] = useState(null);

  const handleContinue = () => {
    if (dynamics.isValid) {
      dispatch(setCartesianCurrentStep(steps.AXIS_SELECTION));
      dispatch(updateConfiguration());
      dispatch(setDynamicsStrokeLength(mechanics.strokeLength));
      const dynamicsHash = getDynamicsHash(dynamics);
      //Reset axis selection and Axis Selection if hash is different
      if (dynamicsHash !== dynamics.hash) {
        dispatch(setAxisSelection(DEFAULT_AXIS_SELECTION));
        dispatch(resetAxesToCompare());
        dispatch(setAxisOptions(DEFAULT_AXIS_OPTIONS));
        dispatch(setDynamicsHash(dynamicsHash));
        dispatch(setFetchedBundles([]));
        dispatch(setConfiguration(null));
      }
    } else {
      dispatch(
        showSnackbar({
          id: "MISSING_OR_INCORRECT_FIELD"
        })
      );
    }
  };

  const handlePrevious = () => {
    dispatch(setCartesianCurrentStep(steps.MECHANICS));
    dispatch(updateConfiguration());
  };

  const validateCycleRunning = () => {
    const { numberOfCyclePerHour, workingHoursPerDay, workingDaysPerYear } = dynamics.cycleRunning;
    return (
      preCalcTotalCycleTime(moves) <= 3600000 &&
      numberOfCyclePerHour !== undefined &&
      numberOfCyclePerHour >= 1 &&
      numberOfCyclePerHour <= maxNumberCyclesPerHour(moves) &&
      workingHoursPerDay !== undefined &&
      workingHoursPerDay >= 1 &&
      workingHoursPerDay <= 24 &&
      workingDaysPerYear !== undefined &&
      workingDaysPerYear >= 1 &&
      workingDaysPerYear <= 365
    );
  };

  const validateCycleDefinition = () => {
    // TODO: these conditions are duplicated in the CycleDefinition component
    let valid = true;
    const maxMovements = newMaxMovements(moves, dynamics.startPosition, mechanics.strokeLength, DIRECTIONS);
    if (dynamics.startPosition < 0 || dynamics.startPosition > mechanics.strokeLength) {
      valid = false;
    }
    let isMovementValid = true;
    dynamics.steps?.forEach((move, index) => {
      if (
        move.stepName === "" ||
        move.stepName === undefined ||
        move.movementType === "" ||
        move.movement === 0 ||
        move.movement === "" ||
        move.movement === undefined ||
        move.movement > maxMovements[index] ||
        move.direction === "" ||
        move.direction === undefined ||
        move.dwellTime === "" ||
        move.dwellTime === undefined ||
        move.dwellTime < 0 ||
        Number(move.travelTime) % 1 !== 0 ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec) ||
        Number(move.tacc) % 1 !== 0 ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec) ||
        Number(move.tdec) % 1 !== 0 ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec) ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec) ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec) ||
        Number(move.travelTime) < Number(move.tacc) + Number(move.tdec)
      ) {
        valid = false;
      }

      if (
        move?.id !== "completionStep" &&
        move.isValid === undefined &&
        !move.isValid &&
        dynamics.strokeLength !== undefined &&
        dynamics.strokeLength !== mechanics.strokeLength
      ) {
        isMovementValid = isMovementValid && false;
      }
    });

    valid = valid && isMovementValid;
    return valid;
  };
  useEffect(() => {
    const isValid = validateCycleRunning() && validateCycleDefinition();
    if (!isReadOnlyMode && isValid !== dynamics?.isValid) {
      dispatch(setDynamics({ ...dynamics, isValid: isValid }));
    }
  }, [dynamics]);

  useEffect(() => {
    const nbMechanicsCarriages = mechanics.quantityCarriages;
    const moves = dynamics.steps.map((move) => ({
      ...move,
      carriages: [...move.carriages]
    }));
    moves.forEach((move) => {
      const moveCarriagesLenth = move.carriages.length;
      if (moveCarriagesLenth > nbMechanicsCarriages) {
        move.carriages.splice(nbMechanicsCarriages);
      }
    });
    dispatch(setDynamics({ ...dynamics, steps: moves }));
  }, [mechanics.quantityCarriages]);

  return (
    <div className="Dynamics flex flex-col space-y-3" {...props}>
      <MechanicsSummary data-testid="mechanics-summary" />
      <div className="h-16" />
      <Suspense fallback={<Spinner data-testid="spinner-suspense" />}>
        <GraphicalChart
          moves={generateMovesData(moves, dynamics.startPosition, DIRECTIONS)}
          setSelectedMove={setSelectedMove}
          selectedMove={selectedMove}
        />
      </Suspense>
      <CycleDefinition setSelectedMove={setSelectedMove} data-testid="cycle-definition" />
      <CycleRunning data-testid="cycle-running" />
      <Navigator
        continueLabel={t("msizer.ui.footer.continue")}
        continueAction={() => {
          handleContinue();
        }}
        continueButtonDisabled={!dynamics.isValid}
        previousAction={() => {
          handlePrevious();
        }}
      />
    </div>
  );
};

export default Dynamics;
